import React from 'react';
import i18next from 'i18next';
import styled from 'styled-components';

import Markdown from 'web-app/react/components/markdown/markdown';
import {type IconProps} from 'web-app/react/components/icons/legacy/icon-props';
import Error from 'web-app/react/components/icons/legacy/lazy-icons/error';
import Info from 'web-app/react/components/icons/legacy/lazy-icons/info';
import {Body} from 'web-app/react/components/text/text';
import {s0, s2} from 'web-app/styleguide/spacing';
import {Flex, Base} from 'web-app/react/components/layout/layout';
import {getProperty} from 'web-app/util/typescript';

export enum NoticeSeverity {
    INFO = 'INFO',
    WARNING = 'WARNING',
    ERROR = 'ERROR',
}

export interface NoticeProps {
    withHeader?: boolean;
    title?: React.ReactNode;
    message?: React.ReactNode | string;
    className?: string;
    onLinkClick?: (e?: any) => any;
    severity: NoticeSeverity;
    'data-e2e-id'?: string;
}

const StyledError = styled(Error).attrs(props => ({
    fill: props.theme.accent3,
}))<IconProps>``;

const StyledWarning = styled(Error).attrs(props => ({
    fill: props.theme.accent2,
}))<IconProps>``;

const StyledInfoIcon = styled(Info).attrs(props => ({
    fill: props.theme.analogue2,
}))<IconProps>``;

const Container = styled('div')<{severity: NoticeSeverity}>`
    display: flex;
    width: 100%;
    border-radius: 4px;
    box-sizing: border-box;
    border: 2px solid
        ${props => {
            switch (props.severity) {
                case NoticeSeverity.INFO:
                    return props.theme.analogue2;
                case NoticeSeverity.ERROR:
                    return props.theme.accent3;
                case NoticeSeverity.WARNING:
                default:
                    return props.theme.accent2;
            }
        }};
    background: ${props => props.theme.invertedText};
`;

const MessageContainer = styled(Base)`
    p {
        margin: 0;
    }
`;

export const getNoticeIcon = (severity: NoticeSeverity, iconSize = 30) => {
    switch (severity) {
        case NoticeSeverity.INFO:
            return <StyledInfoIcon size={iconSize} />;
        case NoticeSeverity.ERROR:
            return <StyledError size={iconSize} />;
        case NoticeSeverity.WARNING:
        default:
            return <StyledWarning size={iconSize} />;
    }
};

const getTitle = (severity: NoticeSeverity) => {
    switch (severity) {
        case NoticeSeverity.INFO:
            return i18next.t('notices.info');
        case NoticeSeverity.ERROR:
            return i18next.t('notices.error');
        case NoticeSeverity.WARNING:
        default:
            return i18next.t('notices.warning');
    }
};

/**
 * @deprecated Use the `Notice` component from Modern Famly
 */
const Notice: React.FC<React.PropsWithChildren<NoticeProps>> = props => {
    const {withHeader = true, title, message, className, severity, onLinkClick, children} = props;

    return (
        <Container severity={severity} className={className} data-e2e-id={getProperty(props, 'data-e2e-id')}>
            <Flex align="center" padding={[s0, s2]}>
                {getNoticeIcon(severity)}
            </Flex>
            <MessageContainer flex={1} padding={[s2, s2, s2, s0]}>
                {withHeader ? <Body emphasized>{title || getTitle(severity)}</Body> : null}
                {typeof message === 'string' ? <Markdown source={message} onLinkClick={onLinkClick} /> : message}
                {children}
            </MessageContainer>
        </Container>
    );
};

export default Notice;
